import CurrencyFormatter from '@components/system/CurrencyFormatter/CurrencyFormatter';
import FieldValue from '@components/system/FieldValue/FieldValue';
import {Card, CardContent, Grid, Link, makeStyles} from '@material-ui/core';
import {KeyboardArrowDown, KeyboardArrowUp} from '@material-ui/icons';
import {
  getPaymentPlanFrequencyLabel,
  PaymentPlanStatus
} from '@shared/constants/Mapping';
import HelperUtils from '@shared/utils/HelperUtils';
import React, {ReactElement} from 'react';
import {useTranslation} from 'react-i18next';
import {APP, DEFAULT_WHITE_COLOR} from 'src/style/variable';
import ActivePaymentPlanDetailsCard from '../ActivePaymentPlanDetailsCard/ActivePaymentPlanDetailsCard';

const PreviousPaymentPlanDetailsCardStyles = makeStyles(() => ({
  root: {
    minWidth: 275
  },
  content: {
    paddingBottom: '16px !important'
  },
  containerWrap: {
    padding: '4px 0'
  },
  DetailsView: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: '1rem',
    rowGap: '1rem',
    overflowY: 'auto',
    maxHeight: '15.5rem'
  },
  previousPaymentPlanMainView: {
    flexDirection: 'row',
    display: 'flex'
  },
  previousPaymentPlanMainViews: {
    flexDirection: 'row',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '0.5rem',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem'
  },
  previousPaymentPlanHeaderValue: {
    color: APP.STATUS.DEFAULT,
    fontSize: '1rem'
    // paddingLeft: '0.5rem'
  },
  previousPaymentPlanType: {
    width: '33%'
  },
  textOverFlow: {
    fontSize: '1rem',
    textOverflow: 'ellipsis',
    overflowWrap: 'break-word',
    paddingRight: '0.2rem',
    textAlign: 'center',
    textTransform: 'capitalize'
  },
  previousPaymentPlanDetailsView: {
    width: '33%'
    // paddingLeft: '0.5rem'
  },
  previousPaymentPlanView: {
    width: '33%'
  },
  applyOddColor: {
    backgroundColor: 'rgb(0,0,0,0.05)'
  },
  applyEvenColor: {
    backgroundColor: DEFAULT_WHITE_COLOR
  },
  NoPreviousPaymentPlanInfoView: {
    flexDirection: 'row',
    display: 'flex'
  },
  divider: {
    marginTop: '0.5rem',
    marginBottom: '0.5rem'
  },
  cardRoot: {
    '& .MuiCardContent-root:last-child': {
      paddingBottom: '13px'
    }
  },
  flexingItems: {
    flex: '1'
  },
  historyArrow: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: '1.1rem'
  },
  arrowLinkStyle: {
    display: 'flex',
    alignItems: 'center'
  },
  textTransformStyle: {
    textTransform: 'capitalize'
  }
}));

// FIXME: ADD PROPER TYPES
interface PreviousPaymentPlanHistoryCardProps {
  item: any;
  totalPaidAmount: any;
  index: number;
  isConvenienceFeeExist?: any;
  isGuarantorPortal: any;
  forceUpdateHandler(): void;
}

const PreviousPaymentPlanHistoryCard = (
  props: PreviousPaymentPlanHistoryCardProps
): ReactElement => {
  const classes = PreviousPaymentPlanDetailsCardStyles();
  const {
    item,
    totalPaidAmount,
    index,
    isConvenienceFeeExist,
    isGuarantorPortal,
    forceUpdateHandler
  } = props;
  const {t} = useTranslation();
  const [arrowUp, setArrowUp] = React.useState(false);
  return (
    <Card
      // here key is nothing but index number is being accepted as a prop from its parent component
      key={index}
      className={[classes.root, 'payment-history-card-wrapper'].join(' ')}
      variant="outlined">
      <CardContent className={classes.content}>
        {/** First Row Container */}
        <Grid container className={classes.containerWrap} spacing={1}>
          <Grid item className={'flexing-items'}>
            <FieldValue
              label={t('PAYMENT_PLANS.START_DATE')}
              value={HelperUtils.formatDisplayDate(item?.instalment_start_date)}
            />
          </Grid>
          <Grid item className={'flexing-items'}>
            <Grid item className={'flexing-items'}>
              <FieldValue
                label={t('PAYMENT_PLANS.TOTAL_INSTALMENTS')}
                value={item?.tenure}
              />
            </Grid>
          </Grid>

          <Grid item className={'flexing-items'}>
            <FieldValue
              label={t('PAYMENT_PLANS.AMOUNT_INSTALMENT')}
              value={
                <CurrencyFormatter
                  value={item?.instalment_amount}
                  displayType={'text'}
                  thousandSeparator={true}
                  prefix={'$'}
                  ignoreRound={true}
                  useSign={true}
                />
              }
            />
          </Grid>
        </Grid>

        {/** Second Row Container (conditional) **/}

        <Grid container className={classes.containerWrap} spacing={1}>
          <Grid item className={'flexing-items'}>
            <FieldValue
              label={t('PAYMENT_PLANS.TOTAL_PAID_AMOUNT')}
              value={
                <CurrencyFormatter
                  value={totalPaidAmount}
                  displayType={'text'}
                  thousandSeparator={true}
                  prefix={'$'}
                  ignoreRound={true}
                  useSign={true}
                />
              }
            />
          </Grid>

          <Grid item className="flexing-items">
            <FieldValue
              label={t('PAYMENT_PLANS.INSTALMENTS_PAID')}
              value={item?.tenure_paid}
            />
          </Grid>

          <Grid item className={'flexing-items'}>
            <FieldValue
              label={t('PAYMENT_PLANS.LAST_INSTALMENT_PAID_DATE')}
              value={HelperUtils.formatDisplayDate(item?.instalment_end_date)}
            />
          </Grid>
        </Grid>

        {/* Third row Container */}
        <Grid container className={classes.containerWrap} spacing={1}>
          <Grid item className={'flexing-items'}>
            <FieldValue
              label={t('PAYMENT_PLANS.COMPLETED_CANCELLED_DATE')}
              value={
                HelperUtils.formatDisplayDate(item?.completed_date) ||
                HelperUtils.formatDisplayDate(item?.cancelled_date)
              }
            />
          </Grid>
          <Grid
            item
            className={[classes.textTransformStyle, classes.flexingItems].join(
              ' '
            )}>
            <FieldValue
              label={t('PAYMENT_PLANS.STATUS')}
              value={item?.status}
            />
          </Grid>
          <Grid
            item
            className={[classes.historyArrow, classes.flexingItems].join(' ')}>
            <Link
              variant="body2"
              className={classes.arrowLinkStyle}
              type={'button'}
              component="button"
              onClick={() => {
                setArrowUp(!arrowUp);
              }}>
              {!arrowUp
                ? t('PAYMENT_PLANS.SHOW_HISTORY')
                : t('PAYMENT_PLANS.HIDE_HISTORY')}
              <Link>
                {!arrowUp && <KeyboardArrowDown />}
                {arrowUp && <KeyboardArrowUp />}
              </Link>
            </Link>
          </Grid>

          <Grid container className={classes.containerWrap} spacing={1}>
            <Grid item className={'flexing-items'}>
              <FieldValue
                label={t('PAYMENT_PLANS.FREQUENCY')}
                value={t(getPaymentPlanFrequencyLabel(item?.frequency_id))}
              />
            </Grid>

            {item?.charge_day ? (
              <Grid item className={'flexing-items'}>
                <FieldValue
                  label={t('PAYMENT_PLANS.INSTALMENT_CHARGE_DAY')}
                  value={HelperUtils.captilizeFirstLetter(item.charge_day)}
                />
              </Grid>
            ) : (
              <Grid item className={'flexing-items'} />
            )}
            <Grid item className={'flexing-items'} />
          </Grid>
        </Grid>
      </CardContent>
      {arrowUp && (
        <ActivePaymentPlanDetailsCard
          showRetryInstalment={
            item?.status === PaymentPlanStatus.CANCELLED ? false : true
          }
          activePlanInstalmentDetails={item?.plan_execution_history}
          isConvenienceFeeExist={isConvenienceFeeExist}
          isGuarantorPortal={isGuarantorPortal}
          forceUpdateHandler={forceUpdateHandler}
        />
      )}
    </Card>
  );
};
export default PreviousPaymentPlanHistoryCard;
